import { onCleanup, onMount } from 'solid-js';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, highlightActiveLine, drawSelection } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { syntaxHighlighting, defaultHighlightStyle, indentOnInput, bracketMatching } from '@codemirror/language';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { oneDark } from '@codemirror/theme-one-dark';
import { yCollab, yUndoManagerKeymap } from 'y-codemirror.next';
import type { CollabContext } from '../collab/yjs';
import { wrapSelection } from './commands';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onView?: (v: EditorView | null) => void;
  collab?: CollabContext | null;
}

export function Editor(props: Props) {
  let hostRef: HTMLDivElement | undefined;
  let view: EditorView | undefined;

  onMount(() => {
    if (!hostRef) return;
    const extensions = [
      history(),
      drawSelection(),
      indentOnInput(),
      bracketMatching(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      keymap.of([
        { key: 'Mod-b', run: (v) => wrapSelection(v, '**'), preventDefault: true },
        { key: 'Mod-i', run: (v) => wrapSelection(v, '*'), preventDefault: true },
        { key: 'Mod-Shift-x', run: (v) => wrapSelection(v, '~~'), preventDefault: true },
        ...defaultKeymap, ...historyKeymap, ...searchKeymap, indentWithTab,
      ]),
      markdown({ base: markdownLanguage, codeLanguages: () => null }),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      EditorView.lineWrapping,
      oneDark,
      EditorView.updateListener.of((u) => {
        if (u.docChanged) props.onChange(u.state.doc.toString());
      }),
      EditorView.theme({
        '&': { height: '100%', fontSize: '14px' },
        '.cm-scroller': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          lineHeight: '1.6',
        },
        '.cm-content': { padding: '16px 0' },
      }),
    ];

    if (props.collab) {
      extensions.push(
        yCollab(props.collab.ytext, props.collab.awareness, { undoManager: props.collab.undoManager }),
        keymap.of(yUndoManagerKeymap),
      );
    }

    const initialDoc = props.collab ? props.collab.ytext.toString() : props.value;
    const startState = EditorState.create({ doc: initialDoc, extensions });
    view = new EditorView({ state: startState, parent: hostRef });
    props.onView?.(view);
  });

  onCleanup(() => {
    props.onView?.(null);
    view?.destroy();
    view = undefined;
  });

  return <div ref={hostRef} class="editor-host" />;
}
